import { Automation, DynamicOptions } from "@/types/v2/Automation";
import {
  AUTOMATION_ACTION_OPTIONS,
  AUTOMATION_TRIGGER_OPTIONS,
} from "@/lib/const";
import { flattenTriggers } from "@/lib/flattenTriggers";
import { flattenActions } from "@/lib/flattenActions";
import type { useDynamicOptions } from "@/hooks/useDynamicOptions";

export const REMAPPABLE_DYNAMIC_OPTIONS: DynamicOptions[] = [
  "containers",
  "technicians",
  "serviceAdvisors",
  "colors",
  // "assignees" is correctly classified as remappable — employee ids are
  // shop-specific. BUT the createTask action nests its assigneeId inside a
  // JSON-encoded actionValue, and the reference-resolver below keys off
  // actionValueOptionsDynamic on the top-level action, so it does not
  // currently detect/remap this nested reference on cross-shop import.
  // Tracked for follow-up in WEBV3-92.
  "assignees",
];

export interface ResolvedReference {
  kind: "trigger" | "action";
  index: number;
  templateId: string;
  dynamicType: DynamicOptions;
  value: string;
  label: string;
}

export type GenerateOptions = ReturnType<
  typeof useDynamicOptions
>["generateOptions"];

export function resolveAutomationReferences(
  automation: Pick<Automation, "triggers" | "actions">,
  generateOptions: GenerateOptions
): ResolvedReference[] {
  const flatTriggers = flattenTriggers(AUTOMATION_TRIGGER_OPTIONS);
  const flatActions = flattenActions(AUTOMATION_ACTION_OPTIONS);
  const refs: ResolvedReference[] = [];

  automation.triggers.forEach((trigger, index) => {
    const template = flatTriggers.find((t) => t.id === trigger.templateId);
    const dynamicType = template?.triggerValueOptionsDynamic;
    if (!dynamicType || !REMAPPABLE_DYNAMIC_OPTIONS.includes(dynamicType))
      return;
    if (trigger.triggerValue === null || trigger.triggerValue === undefined)
      return;

    const options = generateOptions(template?.triggerValueOptions, dynamicType);
    const value = String(trigger.triggerValue);
    const label = options?.find((o) => o.value === value)?.label ?? "";
    refs.push({
      kind: "trigger",
      index,
      templateId: trigger.templateId,
      dynamicType,
      value,
      label,
    });
  });

  automation.actions.forEach((action, index) => {
    const template = flatActions.find((t) => t.id === action.templateId);
    const dynamicType = template?.actionValueOptionsDynamic;
    if (!dynamicType || !REMAPPABLE_DYNAMIC_OPTIONS.includes(dynamicType))
      return;
    if (action.actionValue === null || action.actionValue === undefined) return;

    const options = generateOptions(template?.actionValueOptions, dynamicType);
    const value = String(action.actionValue);
    const label = options?.find((o) => o.value === value)?.label ?? "";
    refs.push({
      kind: "action",
      index,
      templateId: action.templateId,
      dynamicType,
      value,
      label,
    });
  });

  return refs;
}

export interface ExportedAutomation {
  /** The source automation's id — used by import for same-shop update matching. */
  id: string;
  name?: string;
  description?: string;
  enabled: boolean;
  triggersConditional: "AND" | "OR";
  triggers: Automation["triggers"];
  actions: Automation["actions"];
  resolvedReferences: ResolvedReference[];
}

export interface AutomationExportFile {
  schemaVersion: 1;
  exportedAt: string;
  sourceShopId: string;
  automations: ExportedAutomation[];
}

type ExportableAutomation = Pick<
  Automation,
  | "id"
  | "name"
  | "description"
  | "enabled"
  | "triggersConditional"
  | "triggers"
  | "actions"
>;

export function buildAutomationExport(
  automations: ExportableAutomation[],
  shopId: string,
  generateOptions: GenerateOptions
): AutomationExportFile {
  return {
    schemaVersion: 1,
    exportedAt: new Date().toISOString(),
    sourceShopId: shopId,
    automations: automations.map((automation) => ({
      id: automation.id,
      name: automation.name,
      description: automation.description,
      enabled: automation.enabled,
      triggersConditional: automation.triggersConditional,
      triggers: automation.triggers,
      actions: automation.actions,
      resolvedReferences: resolveAutomationReferences(
        automation,
        generateOptions
      ),
    })),
  };
}

export function downloadAutomationExport(
  file: AutomationExportFile,
  filename: string
): void {
  const blob = new Blob([JSON.stringify(file, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
